// src/components/AdminLogin.js
// UPDATED: 2026-08-20 — dispatches LOGIN_SUCCESS (and RESET_CHATBOT, mirroring
// AuthWrapper's startup pattern) to Redux immediately on successful login.
// Previously this component only called navigate() on success, so Redux
// auth state stayed whatever it was before login until AuthWrapper's
// checkSession() ran again on the next full page load/refresh — meaning
// systemRole-gated UI (e.g. the AI Cost Governance nav link) and every
// other admin-only nav item wouldn't appear until then.
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/admin/login`,
        { email, password },
        { withCredentials: true }
      );

      if (res.data && res.data.user && res.data.user.accountType === 'Admin') {
        console.log('[AdminLogin] Server response.user →', res.data.user);

        // ⭐ Populate Redux immediately — don't wait for the next
        // AuthWrapper checkSession() (which only runs on app mount).
        // adminLogin.js's response.user already includes systemRole, so
        // this is enough for isSystemAdmin-gated nav links to appear right
        // away, same as isAdmin-gated ones.
        const loginPayload = {
          user: {
            ...res.data.user,
            isAuthenticated: true,
          },
        };
        console.log('[AdminLogin][Redux] Dispatching LOGIN_SUCCESS — systemRole:',
          res.data.user.systemRole ?? '(none)');
        console.log('[AdminLogin][Redux]   payload →', loginPayload);

        dispatch({ type: 'LOGIN_SUCCESS', payload: loginPayload });
        dispatch({
          type: 'RESET_CHATBOT',
          payload: {
            isAuthenticated: true,
            hasSubscribed: false,
          },
        });

        toast.success('Login successful!');
        navigate('/admin-dashboard');
      } else {
        console.warn('[AdminLogin] Login response missing user or accountType !== Admin →', res.data);
        toast.error('Invalid admin credentials.');
      }
    } catch (err) {
      console.error('[AdminLogin] Login error:', err);
      
      // Handle unverified admin
      if (err.response?.data?.requiresVerification && err.response?.data?.adminId) {
        toast.warning('Please verify your email first');
        navigate('/admin/verify', {
          state: {
            adminId: err.response.data.adminId,
            email: email
          }
        });
      } else {
        toast.error(err.response?.data?.message || 'Admin login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: '4rem auto', padding: '2rem', border: '1px solid #ccc', borderRadius: '6px' }}>
      <h2 style={{ marginBottom: '1rem' }}>Admin Login</h2>
      <form onSubmit={handleAdminLogin}>
        <div style={{ marginBottom: '1rem' }}>
          <label>Email:</label>
          <input
            type="email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Password:</label>
          <input
            type="password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{
            backgroundColor: '#007bff',
            color: '#fff',
            padding: '0.6rem 1.2rem',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            width: '100%'
          }}
        >
          {loading ? 'Logging in...' : 'Login as Admin'}
        </button>
      </form>
      
      <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '14px' }}>
        Don't have an account?{' '}
        <a href="/admin/register" style={{ color: '#007bff', textDecoration: 'none' }}>
          Register here
        </a>
      </div>
    </div>
  );
};

export default AdminLogin;